import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@src/api/config";

export type BusinessDashboardPeriod = "daily" | "weekly" | "monthly" | "yearly";

type DashboardSnapshot = {
  summary: {
    businessName: string;
    completion: number;
  };
  certificates: {
    total: number;
    growth: string;
    innerText: string;
    stats: Array<{ label: string; value: number; color: string }>;
  };
  topClients: {
    labels: string[];
    values: number[];
  };
  topContractors: Array<{
    id?: number | string;
    name: string;
    razon_social: string;
    total_projects: number;
  }>;
};

export interface BusinessDashboardState {
  loading: boolean;
  period: BusinessDashboardPeriod;
  snapshots: Record<BusinessDashboardPeriod, DashboardSnapshot>;
}

const emptyCertificates = {
  total: 0,
  growth: "",
  innerText: "Certificados emitidos",
  stats: [
    { label: "Emitido", value: 0, color: "#00CFE8" },
    { label: "Con factura", value: 0, color: "#7367F0" },
    { label: "Pagado", value: 0, color: "#20C997" },
    { label: "Cancelado", value: 0, color: "#EA5455" },
    { label: "Comisionado", value: 0, color: "#4b4b4b" }
  ]
};

const createEmptyBusinessSnapshot = (): DashboardSnapshot => ({
  summary: {
    businessName: "Empresas",
    completion: 0
  },
  certificates: emptyCertificates,
  topClients: {
    labels: [],
    values: []
  },
  topContractors: []
});

const businessSnapshots: Record<BusinessDashboardPeriod, DashboardSnapshot> = {
  daily: createEmptyBusinessSnapshot(),
  weekly: createEmptyBusinessSnapshot(),
  monthly: createEmptyBusinessSnapshot(),
  yearly: createEmptyBusinessSnapshot()
};

const initialState: BusinessDashboardState = {
  loading: false,
  period: "monthly",
  snapshots: businessSnapshots
};

const defaultRangeDates = [
  "2024-01-01T06:00:00.000Z",
  "2026-06-19T06:00:00.000Z"
];

const getRows = (payload: any) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getLabel = (item: any) => (
  item?.label ??
  item?.name ??
  item?.nombre ??
  item?.name_company_client ??
  item?.name_contractor ??
  item?.company_client?.name ??
  item?.contractor?.name_contractor ??
  item?.user?.full_name ??
  "-"
);

const getValue = (item: any) => Number(
  item?.value ??
  item?.total_projects ??
  item?.projects ??
  item?.total ??
  item?.count ??
  0
);

const normalizeTopChart = (payload: any) => {
  const data = payload?.data ?? payload;
  const chartData = data?.topClients ?? data?.topContractors ?? data;

  if (Array.isArray(chartData?.labels) && Array.isArray(chartData?.values)) {
    return {
      labels: chartData.labels,
      values: chartData.values
    };
  }

  const rows = getRows(chartData);
  return {
    labels: rows.map(getLabel),
    values: rows.map(getValue)
  };
};

const normalizeTopContractors = (payload: any) => {
  const data = payload?.data ?? payload;
  const rows = getRows(data?.topContractors ?? data);

  return rows.map((item: any) => ({
    id:
      item?.id ??
      item?.contractor_id ??
      item?.contractor?.id ??
      item?.contractor?.value,
    name: getLabel(item),
    razon_social:
      item?.razon_social ??
      item?.business_name ??
      item?.tax_data?.business_name ??
      "-",
    total_projects: getValue(item)
  }));
};

const normalizeCertificates = (payload: any) => {
  const data = payload?.data ?? payload;
  const stats = [
    { label: "Emitido", value: data?.issued?.total ?? 0, color: "#00CFE8" },
    { label: "Con factura", value: data?.withInvoice?.total ?? 0, color: "#7367F0" },
    { label: "Pagado", value: data?.withReference?.total ?? 0, color: "#20C997" },
    { label: "Cancelado", value: data?.cancelled?.total ?? 0, color: "#EA5455" },
    { label: "Comisionado", value: data?.commissioned?.total ?? 0, color: "#4b4b4b" }
  ];
  const total = data?.total?.total ?? data?.total ?? stats.reduce(
    (sum: number, item: { value: number }) => sum + item.value,
    0
  );

  return {
    total,
    growth: data?.growth ?? "",
    innerText: "Certificados emitidos",
    stats
  };
};

export const getBusinessDashboard = createAsyncThunk(
  "businessDashboard/get",
  async (params: Record<string, any> = {}) => {
    const response = await api.get("api/dashboard/businesses", { params });
    return {
      data: response.data
    };
  }
);

export const getBusinessDashboardCertificates = createAsyncThunk(
  "businessDashboard/certificates",
  async (params: Record<string, any> = {}, { getState }: any) => {
    const state = getState();
    const { productSelected } = state.products || {};
    const { businessLineSelected } = state.businessLines || {};
    const query = {
      period: "yearly",
      ...params,
      business_line: params.business_line ?? businessLineSelected?.id ?? 1,
      product: params.product ?? productSelected?.value ?? 3
    };
    const response = await api.get("api/dashboard/certificates", { params: query });
    return {
      data: response.data,
      period: query.period
    };
  }
);

export const getBusinessDashboardTopClients = createAsyncThunk(
  "businessDashboard/topClients",
  async (params: Record<string, any> = {}) => {
    const query = {
      period: "daily",
      rangeDates: defaultRangeDates,
      ...params
    };
    const response = await api.get("api/dashboard_rc/company_client", {
      params: query
    });
    return {
      data: response.data,
      period: query.period
    };
  }
);

export const getBusinessDashboardTopContractors = createAsyncThunk(
  "businessDashboard/topContractors",
  async (params: Record<string, any> = {}) => {
    const query = {
      period: "yearly",
      rangeDates: defaultRangeDates,
      ...params
    };
    const response = await api.get("api/dashboard_rc/contractors", {
      params: query
    });
    return {
      data: response.data,
      period: query.period
    };
  }
);

const businessDashboardSlice = createSlice({
  name: "businessDashboard",
  initialState,
  reducers: {
    setPeriod: (state, action) => {
      state.period = action.payload;
    },
    resetBusinessDashboard: (state) => {
      state.loading = false;
      state.period = "monthly";
      state.snapshots = businessSnapshots;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getBusinessDashboard.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBusinessDashboard.fulfilled, (state, action) => {
      state.loading = false;
      const payload = action.payload?.data;
      if (payload?.period && payload?.snapshots) {
        state.period = payload.period;
        state.snapshots = payload.snapshots;
      }
    });
    builder.addCase(getBusinessDashboard.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getBusinessDashboardCertificates.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBusinessDashboardCertificates.fulfilled, (state, action) => {
      state.loading = false;
      const period = action.payload.period as BusinessDashboardPeriod;
      state.snapshots[period] = {
        ...state.snapshots[period],
        certificates: normalizeCertificates(action.payload.data)
      };
    });
    builder.addCase(getBusinessDashboardCertificates.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getBusinessDashboardTopClients.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBusinessDashboardTopClients.fulfilled, (state, action) => {
      state.loading = false;
      const period = action.payload.period as BusinessDashboardPeriod;
      state.snapshots[period] = {
        ...state.snapshots[period],
        topClients: normalizeTopChart(action.payload.data)
      };
    });
    builder.addCase(getBusinessDashboardTopClients.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getBusinessDashboardTopContractors.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBusinessDashboardTopContractors.fulfilled, (state, action) => {
      state.loading = false;
      const period = action.payload.period as BusinessDashboardPeriod;
      state.snapshots[period] = {
        ...state.snapshots[period],
        topContractors: normalizeTopContractors(action.payload.data)
      };
    });
    builder.addCase(getBusinessDashboardTopContractors.rejected, (state) => {
      state.loading = false;
    });
  }
});

export const { setPeriod, resetBusinessDashboard } = businessDashboardSlice.actions;

export default businessDashboardSlice.reducer;
